import "@/App.css";
import { useState, useEffect } from "react";
import { 
  Droplets, 
  FlaskConical, 
  Factory, 
  Globe, 
  Shield, 
  Leaf,
  ChevronDown,
  Menu,
  X,
  MapPin,
  Mail,
  ArrowRight,
  Cog,
  Zap,
  Beaker,
  BarChart3
} from "lucide-react";

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: "#about", label: "About" },
    { href: "#companies", label: "Companies" },
    { href: "#services", label: "Services" },
    { href: "#contact", label: "Contact" }
  ];

  return (
    <header 
      data-testid="header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white shadow-md' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <a href="#" data-testid="logo-link" className="flex items-center gap-3">
          <Droplets className={`w-8 h-8 ${isScrolled ? 'text-[#2E6F95]' : 'text-[#7FBBD4]'}`} />
          <span className={`font-serif font-bold text-xl tracking-tight ${isScrolled ? 'text-[#0D2818]' : 'text-white'}`}>
            IES Global
          </span>
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-${link.label.toLowerCase()}`}
              className={`text-sm font-medium transition-colors ${
                isScrolled ? 'text-[#0D2818]/70 hover:text-[#0D2818]' : 'text-white/70 hover:text-white'
              }`}
            >
              {link.label}
            </a>
          ))}
          <a 
            href="#contact"
            data-testid="nav-cta-button"
            className="bg-[#2E6F95] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#2E6F95]/80 transition-colors"
          >
            Get Started
          </a>
        </nav>

        {/* Mobile Menu Button */}
        <button
          data-testid="mobile-menu-button"
          className={`lg:hidden p-2 ${isScrolled ? 'text-[#0D2818]' : 'text-white'}`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t">
          <nav className="max-w-7xl mx-auto px-4 py-6 flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                className="text-base font-medium text-[#0D2818]/70 hover:text-[#0D2818] py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a 
              href="#contact"
              data-testid="mobile-nav-cta-button"
              className="bg-[#2E6F95] text-white px-6 py-3 text-sm font-semibold text-center mt-2"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Get Started
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section
const Hero = () => {
  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src="https://images.pexels.com/photos/12975469/pexels-photo-12975469.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt="Water treatment facility"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D2818]/95 via-[#0D2818]/75 to-[#0D2818]/40" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        <div className="max-w-3xl">
          <div className="mb-6">
            <span className="inline-block px-4 py-1.5 bg-[#2E6F95]/20 border border-[#2E6F95]/40 text-[#7FBBD4] text-sm font-medium tracking-wider uppercase">
              Integrated Environmental Solutions
            </span>
          </div>
          
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-[1.1] mb-6">
            Three Ways. <span className="text-[#B87333]">One Complete</span> Solution.
          </h1>
          
          <p className="text-lg sm:text-xl text-white/70 leading-relaxed mb-10 max-w-2xl">
            A strategic alliance of engineering, manufacturing, and laboratory companies delivering 
            end-to-end water and environmental solutions across United States and Mexico.
          </p>
          
          <div className="flex flex-wrap gap-4">
            <a 
              href="#about"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#2E6F95] text-white font-semibold text-base hover:bg-[#2E6F95]/80 transition-colors duration-300"
            >
              Discover Our Ecosystem
              <ArrowRight className="w-5 h-5" />
            </a>
            <a 
              href="#contact"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white font-semibold text-base hover:border-[#B87333] hover:text-[#B87333] transition-colors duration-300"
            >
              Request a Proposal
            </a>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-3 gap-6 sm:gap-8 pt-10 border-t border-white/10">
            {[
              { value: "23+", label: "Years of Experience" },
              { value: "3,000+", label: "Clients Served" },
              { value: "3", label: "Specialized Companies" }
            ].map((stat, index) => (
              <div key={index}>
                <div className="font-serif text-3xl sm:text-4xl font-bold text-[#B87333]">{stat.value}</div>
                <div className="text-sm text-white/50 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <a href="#about" className="flex flex-col items-center gap-2 text-white/40 hover:text-white/60 transition-colors">
          <span className="text-xs tracking-widest uppercase">Scroll</span>
          <ChevronDown className="w-5 h-5 animate-bounce" />
        </a>
      </div>
    </section>
  );
};

// About Section
const About = () => {
  const features = [
    {
      icon: Globe,
      title: "Global Reach",
      description: "Operations in United States and Mexico with cross-border technology transfer capabilities."
    },
    {
      icon: Shield,
      title: "Vertically Integrated",
      description: "From project engineering to equipment manufacturing, laboratory analysis, and ongoing operation — all under one umbrella."
    },
    {
      icon: Leaf,
      title: "ESG Committed",
      description: "Carbon credits, biogas energy recovery, and water reuse solutions aligned with global sustainability standards."
    }
  ];

  return (
    <section id="about" data-testid="about-section" className="py-24 lg:py-32 bg-[#F5F0EB]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-16">
          <span className="text-[#B87333] text-sm font-semibold tracking-wider uppercase">About IES Global</span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0D2818] mt-3 leading-tight">
            Engineering the Future of Water
          </h2>
          <div className="w-16 h-1 bg-[#B87333] mt-6 mb-6"></div>
          <p className="text-lg text-[#0D2818]/70 leading-relaxed">
            IES Global unites three specialized solutions into a single, vertically integrated platform. 
            From advanced engineering and industrial manufacturing to environmental monitoring, 
            we deliver complete solutions that no single company can match alone.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-3 relative">
            <img 
              src="https://images.pexels.com/photos/4627270/pexels-photo-4627270.jpeg?auto=compress&cs=tinysrgb&w=800"
              alt="Clean water stream in nature"
              className="w-full h-[400px] lg:h-[500px] object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0D2818]/80 to-transparent p-8">
              <p className="text-white font-serif text-2xl italic">"Transforming Water. Sustaining Tomorrow."</p>
            </div>
          </div>
          
          <div className="lg:col-span-2 space-y-6">
            {features.map((feature, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#2E6F95]/10 flex items-center justify-center text-[#2E6F95]">
                  <feature.icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0D2818] text-lg">{feature.title}</h3>
                  <p className="text-[#0D2818]/60 text-sm leading-relaxed mt-1">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Companies Section
const Companies = () => {
  const companies = [
    {
      name: "Engineering & Project Development",
      established: "",
      category: "Engineering & Project Development",
      categoryColor: "#00B4D8",
      description: "Specializes in EPC modular water and wastewater treatment systems, including patented mBBR Hybrid technology.",
      features: ["Patented mBBR Hybrid", "EPC Modular Systems"]
    },
    {
      name: "Industrial Manufacturing & Energy",
      established: "",
      category: "Industrial Manufacturing & Energy",
      categoryColor: "#E8A838",
      description: "Full-scale manufacturing facility producing 50+ equipment types for water treatment, wastewater, and biogas energy recovery. Vertically integrated with in-house engineering, CNC machining, and quality control.",
      features: ["50+ Equipment Types", "Biogas & Solar Energy"]
    },
    {
      name: "Environmental Monitoring & Analysis",
      established: "",
      category: "Environmental Monitoring & Analysis",
      categoryColor: "#2D8B4E",
      description: "ISO 17025 accredited laboratory with IAAC scope, serving 1,039+ active clients across 12 industrial sectors.",
      features: ["ISO 17025 + IAAC", "1,039+ Active Clients", "104 Employees"]
    }
  ];

  return (
    <section id="companies" data-testid="companies-section" className="py-24 lg:py-32 bg-[#0D2818]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#B87333] text-sm font-semibold tracking-wider uppercase">Our Ecosystem</span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-3">The Alliance</h2>
          <div className="w-16 h-1 bg-[#B87333] mx-auto mt-6"></div>
        </div>

        <div className="space-y-8">
          {companies.map((company, index) => (
            <div 
              key={company.name}
              data-testid={`company-card-${index}`}
              className="p-8 lg:p-10"
              style={{ 
                backgroundColor: `${company.categoryColor}08`,
                borderLeft: `4px solid ${company.categoryColor}`
              }}
            >
              <div className="grid lg:grid-cols-12 gap-8 items-start">
                <div className="lg:col-span-3 flex flex-col items-start gap-3">
                  {company.established && <p className="text-white/40 text-sm">{company.established}</p>}
                </div>
                
                <div className="lg:col-span-5">
                  <div 
                    className="text-sm font-semibold tracking-wider uppercase mb-2"
                    style={{ color: company.categoryColor }}
                  >
                    {company.category}
                  </div>
                  <p className="text-white/70 leading-relaxed">{company.description}</p>
                </div>
                
                <div className="lg:col-span-4">
                  <div className="space-y-3">
                    {company.features.map((feature, fIndex) => (
                      <div key={fIndex} className="flex items-center gap-3 text-white/80">
                        <div 
                          className="w-2 h-2 flex-shrink-0"
                          style={{ backgroundColor: company.categoryColor }}
                        ></div>
                        <span className="text-sm font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const Services = () => {
  const services = [
    {
      icon: Cog,
      title: "EPC & Modular Systems",
      image: "https://images.pexels.com/photos/35425759/pexels-photo-35425759.jpeg?auto=compress&cs=tinysrgb&w=600",
      description: "Turnkey water and wastewater treatment plants designed, manufactured, and commissioned as modular, containerized units for rapid deployment."
    },
    {
      icon: FlaskConical,
      title: "Environmental Monitoring",
      image: "https://images.pexels.com/photos/8326317/pexels-photo-8326317.jpeg?auto=compress&cs=tinysrgb&w=600",
      description: "ISO 17025 accredited laboratory analysis, field sampling, and real-time environmental monitoring across 12 industrial sectors."
    },
    {
      icon: Factory,
      title: "Equipment Manufacturing",
      image: "https://images.pexels.com/photos/29988964/pexels-photo-29988964.jpeg?auto=compress&cs=tinysrgb&w=600",
      description: "Third-party manufacturing of 50+ equipment types including reactors, clarifiers, filters, and complete treatment systems."
    }
  ];

  const additionalServices = [
    { icon: Zap, title: "Biogas & Energy", description: "Biogas recovery and solar energy integration for self-sustaining treatment plants." },
    { icon: Beaker, title: "Chemical Solutions", description: "Custom chemical treatment programs for industrial water and effluent optimization." },
    { icon: BarChart3, title: "Digital Twins", description: "Real-time process simulation and predictive maintenance using IoT and AI." },
    { icon: Shield, title: "Compliance & ESG", description: "Regulatory compliance, carbon credit programs, and environmental reporting." }
  ];

  return (
    <section id="services" data-testid="services-section" className="py-24 lg:py-32 bg-[#F5F0EB]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-16">
          <span className="text-[#B87333] text-sm font-semibold tracking-wider uppercase">What We Do</span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0D2818] mt-3">End-to-End Capabilities</h2>
          <div className="w-16 h-1 bg-[#B87333] mt-6"></div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div key={index} data-testid={`service-card-${index}`} className="group">
              <div className="relative h-64 overflow-hidden mb-6">
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D2818]/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <service.icon className="w-8 h-8" />
                </div>
              </div>
              <h3 className="font-serif text-xl font-bold text-[#0D2818] mb-3">{service.title}</h3>
              <p className="text-[#0D2818]/60 leading-relaxed text-sm">{service.description}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {additionalServices.map((service, index) => (
            <div key={index} className="p-6 bg-white border border-[#0D2818]/10">
              <div className="text-[#2E6F95] mb-4">
                <service.icon className="w-5 h-5" />
              </div>
              <h4 className="font-semibold text-[#0D2818] mb-2">{service.title}</h4>
              <p className="text-sm text-[#0D2818]/60 leading-relaxed">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Technologies Section
const Technologies = () => {
  const technologies = [
    {
      name: "Cembrane SiC Membranes",
      origin: "Denmark",
      metric: "0.1 μm precision",
      description: "Silicon carbide ceramic membranes with unmatched durability and filtration precision. Exclusive distribution in Brazil."
    },
    {
      name: "mBBR Hybrid (Patented)",
      origin: "Brazil",
      metric: "40% smaller footprint",
      description: "Moving Bed Biofilm Reactor combined with membrane technology for compact, high-efficiency biological treatment."
    },
    {
      name: "PAO Systems",
      origin: "Advanced Oxidation",
      metric: "99.5% removal rate",
      description: "Pulsed Air Oxidation technology for treating high-complexity industrial effluents with superior removal rates."
    },
    {
      name: "IoT Monitoring Platform",
      origin: "Brazil",
      metric: "24/7 real-time data",
      description: "Real-time environmental monitoring with AI-powered analytics, automated alerts, and regulatory compliance dashboards."
    }
  ];

  return (
    <section id="technologies" data-testid="technologies-section" className="py-24 lg:py-32 bg-[#0D2818] relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <pattern id="topo" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
            <circle cx="100" cy="100" r="80" fill="none" stroke="white" strokeWidth="0.5"/>
            <circle cx="100" cy="100" r="60" fill="none" stroke="white" strokeWidth="0.5"/>
            <circle cx="100" cy="100" r="40" fill="none" stroke="white" strokeWidth="0.5"/>
          </pattern>
          <rect width="100%" height="100%" fill="url(#topo)"/>
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#B87333] text-sm font-semibold tracking-wider uppercase">Innovation</span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-3">Proprietary Technologies</h2>
          <div className="w-16 h-1 bg-[#B87333] mx-auto mt-6"></div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {technologies.map((tech, index) => (
            <div 
              key={index}
              data-testid={`tech-card-${index}`}
              className="border border-white/10 p-8 hover:border-[#2E6F95]/40 transition-colors duration-500 group"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-serif text-xl font-bold text-white group-hover:text-[#B87333] transition-colors duration-300">
                    {tech.name}
                  </h3>
                  <span className="text-xs text-[#2E6F95] font-medium tracking-wider uppercase mt-1 inline-block">
                    Origin: {tech.origin}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-[#B87333] font-serif text-lg font-bold">{tech.metric}</span>
                </div>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">{tech.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        setError('Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 bg-[#0D2818]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <span className="text-[#B87333] text-sm font-semibold tracking-wider uppercase">Get in Touch</span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-3 leading-tight">
              Let's Build Something Together
            </h2>
            <div className="w-16 h-1 bg-[#B87333] mt-6 mb-8"></div>
            <p className="text-white/60 leading-relaxed mb-10">
              Whether you're looking for a complete water treatment solution, environmental monitoring services, 
              or exploring partnership opportunities, our team is ready to help.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-[#2E6F95] mt-1 flex-shrink-0" />
                <div>
                  <div className="text-white font-medium">Headquarters</div>
                  <div className="text-white/50 text-sm">Florida, United States</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Globe className="w-5 h-5 text-[#2E6F95] mt-1 flex-shrink-0" />
                <div>
                  <div className="text-white font-medium">Markets</div>
                  <div className="text-white/50 text-sm">USA · Mexico</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="w-5 h-5 text-[#2E6F95] mt-1 flex-shrink-0" />
                <div>
                  <div className="text-white font-medium">Email</div>
                  <div className="text-white/50 text-sm">contact@iesglobal.tech</div>
                </div>
              </div>
            </div>
          </div>

          <div>
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12 border border-white/10 p-8">
                <div className="w-16 h-16 bg-[#2E6F95] rounded-full flex items-center justify-center mb-6">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-white mb-3">Message Sent!</h3>
                <p className="text-white/60">Thank you for reaching out. We'll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="text-white/60 text-sm font-medium block mb-2">Full Name *</label>
                    <input
                      type="text"
                      data-testid="contact-name-input"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:border-[#2E6F95] focus:outline-none transition-colors"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-sm font-medium block mb-2">Email *</label>
                    <input
                      type="email"
                      data-testid="contact-email-input"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:border-[#2E6F95] focus:outline-none transition-colors"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-white/60 text-sm font-medium block mb-2">Company</label>
                  <input
                    type="text"
                    data-testid="contact-company-input"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:border-[#2E6F95] focus:outline-none transition-colors"
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <label className="text-white/60 text-sm font-medium block mb-2">Message *</label>
                  <textarea
                    data-testid="contact-message-input"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:border-[#2E6F95] focus:outline-none transition-colors resize-none"
                    placeholder="Tell us about your project or inquiry..."
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-[#2E6F95] text-white font-semibold text-base hover:bg-[#2E6F95]/80 transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  {!isSubmitting && <ArrowRight className="w-5 h-5" />}
                </button>
                {error && (
                  <p className="text-red-400 text-sm text-center mt-2">{error}</p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#071810] py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Droplets className="w-6 h-6 text-[#2E6F95]" />
            <span className="font-serif font-bold text-lg text-white">IES Global</span>
          </div>
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Integrated Environmental Solutions Global. All rights reserved.
          </p>
          <p className="text-white/40 text-sm italic">
            Transforming Water. Sustaining Tomorrow.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Header />
      <main>
        <Hero />
        <About />
        <Companies />
        <Services />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
